import { useQuery } from "@tanstack/vue-query";
import { BookingOrdersApi, ItinerariesApi } from "@xs2event/js-lib/api";
import { computed, unref, type MaybeRef } from "vue";

const itinerariesApi = new ItinerariesApi();
const bookingordersApi = new BookingOrdersApi();

export const useItineraries = (items: MaybeRef<{ bookingorderId: string; ticketId: string }[]>) => {
  const uniqueItems = computed(() => {
    const seen = new Set<string>();
    return unref(items).filter(({ ticketId }) => {
      if (seen.has(ticketId)) return false;
      seen.add(ticketId);
      return true;
    });
  });

  // Stable string key so the same set of IDs always hits the same cache entry
  const queryKey = computed(() => {
    const ids = uniqueItems.value
      .map((i) => `${i.bookingorderId}:${i.ticketId}`)
      .sort()
      .join(",");
    return ["itineraries", ids];
  });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const itineraries = await Promise.all(
        uniqueItems.value.map(async (item) => ({ ...item, ...(await itinerariesApi.itineraryshareGet(item)) }))
      );

      return itineraries;
    },
    staleTime: Infinity,
  });

  const isLoading = computed(() => query.isFetching.value);

  const openItineraries = () => {
    bookingordersApi.bookingitinerarysharelinkPost(validItineraries.value[0]).then((response) => {
      window.open(response.itineraryLink, "_blank");
    });
  };

  const getShareLink = async (): Promise<string> => {
    const response = await bookingordersApi.bookingitinerarysharelinkPost(validItineraries.value[0]);
    return response.itineraryLink;
  };

  const validItineraries = computed(() => {
    return (
      query.data.value?.filter((itinerary) => {
        if (
          !itinerary.colors?.primaryColorCode ||
          !itinerary.colors?.secondaryColorCode ||
          !itinerary.colors?.ternaryColorCode
        ) {
          return false;
        }

        if (!itinerary.gettingThere || Object.values(itinerary.gettingThere).some((value) => !value)) {
          return false;
        }

        return true;
      }) || []
    );
  });

  const { data: itineraries } = query;

  return { isLoading, itineraries, openItineraries, validItineraries, getShareLink };
};
